import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import SocketService from "network/SocketService";
import SocketProtocol from "network/SocketProtocol";
import { getReconnectThreshold } from "config/reconnectionConfig";
import ReconnectionDialog from "components/common/ReconnectionDialog";

interface SocketContextType {
  isConnected: boolean;
  reconnect: () => void;
  disconnect: () => void;
  markIntentionalExit: () => void; // 의도적 나가기 플래그 설정
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // 초기값을 true로 설정
  const [isConnected, setIsConnected] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // 하이브리드 재연결 전략 상태
  const lastDisconnectTime = useRef(0);
  const [showReconnectDialog, setShowReconnectDialog] = useState(false);
  const [disconnectDuration, setDisconnectDuration] = useState(0);

  useEffect(() => {
    const socket = SocketService.getInstance();

    // 실제 연결 상태 확인 후 상태 업데이트
    const checkConnection = () => {
      const actualConnection = socket.isConnected();
      setIsConnected(actualConnection);
      console.log("Socket connection status:", actualConnection);
      setIsInitialized(true);
    };

    // 초기 연결 상태 확인
    checkConnection();

    // 연결 상태 변경 리스너 등록
    const connectionListener = (connected: boolean) => {
      if (isInitialized) {
        setIsConnected(connected);
        console.log("Socket connection status changed:", connected);

        // 끊김 시간 기록
        if (!connected) {
          lastDisconnectTime.current = Date.now();
          console.log("Disconnected at:", lastDisconnectTime.current);
        }

        // 재연결 성공 시 하이브리드 전략 적용
        if (connected && lastDisconnectTime.current > 0) {
          handleReconnection(socket);
        }
      }
    };

    // 하이브리드 재연결 전략
    const handleReconnection = (socket: SocketService) => {
      // 1단계: 의도적 이탈 플래그 확인
      const intentionalExit = localStorage.getItem("intentionalExit");
      if (intentionalExit === "true") {
        console.log("Intentional exit - skipping reconnection");
        localStorage.removeItem("intentionalExit");
        return;
      }

      // 2단계: 게임 중이었는지 확인
      const gameInfoStr = localStorage.getItem("currentGameInfo");
      if (!gameInfoStr) {
        return;
      }

      let gameInfo;
      try {
        gameInfo = JSON.parse(gameInfoStr);
      } catch (error) {
        console.error("Failed to parse game info:", error);
        return;
      }

      if (!gameInfo.isInGame || !gameInfo.recreationId || !gameInfo.roomName) {
        return;
      }

      // 3단계: 끊김 시간 계산
      const duration = Date.now() - lastDisconnectTime.current;
      console.log(`Disconnected for ${duration}ms`);

      // 4단계: 임계값 확인 (게임별 설정)
      const threshold = getReconnectThreshold(gameInfo.recreationId);

      if (duration < threshold) {
        // 짧은 끊김 → 자동 재접속
        console.log(`Auto reconnecting (duration: ${duration}ms, threshold: ${threshold}ms)`);
        socket.emit(
          SocketProtocol.REQUEST_GAME_STATE,
          gameInfo.recreationId,
          gameInfo.roomName
        );
      } else {
        // 긴 끊김 → 다이얼로그 표시
        console.log(`Manual reconnection required (duration: ${duration}ms, threshold: ${threshold}ms)`);
        setDisconnectDuration(duration);
        setShowReconnectDialog(true);
      }
    };

    socket.addConnectionListener(connectionListener);

    return () => {
      socket.removeConnectionListener(connectionListener);
    };
  }, [isInitialized]);

  const reconnect = () => {
    const socket = SocketService.getInstance();
    if (!socket.isConnected()) {
      socket.connect();
    }
  };

  const disconnect = () => {
    const socket = SocketService.getInstance();
    socket.disconnect();
  };

  const markIntentionalExit = () => {
    console.log("Marking intentional exit");
    localStorage.setItem("intentionalExit", "true");

    // 게임 정보 삭제
    const gameInfoStr = localStorage.getItem("currentGameInfo");
    if (gameInfoStr) {
      try {
        const gameInfo = JSON.parse(gameInfoStr);
        gameInfo.isInGame = false;
        localStorage.setItem("currentGameInfo", JSON.stringify(gameInfo));
      } catch (error) {
        console.error("Failed to update game info:", error);
      }
    }
  };

  // 다이얼로그 재연결 핸들러
  const handleDialogReconnect = () => {
    const socket = SocketService.getInstance();
    const gameInfoStr = localStorage.getItem("currentGameInfo");

    if (gameInfoStr) {
      try {
        const gameInfo = JSON.parse(gameInfoStr);
        if (gameInfo.isInGame && gameInfo.recreationId && gameInfo.roomName) {
          console.log("Manual reconnection confirmed");
          socket.emit(
            SocketProtocol.REQUEST_GAME_STATE,
            gameInfo.recreationId,
            gameInfo.roomName
          );
        }
      } catch (error) {
        console.error("Failed to parse game info:", error);
      }
    }

    setShowReconnectDialog(false);
  };

  // 다이얼로그 취소 핸들러
  const handleDialogCancel = () => {
    console.log("Manual reconnection cancelled");

    // 게임 정보 삭제
    const gameInfoStr = localStorage.getItem("currentGameInfo");
    if (gameInfoStr) {
      try {
        const gameInfo = JSON.parse(gameInfoStr);
        gameInfo.isInGame = false;
        localStorage.setItem("currentGameInfo", JSON.stringify(gameInfo));
      } catch (error) {
        console.error("Failed to update game info:", error);
      }
    }

    setShowReconnectDialog(false);
  };

  return (
    <SocketContext.Provider
      value={{
        isConnected: isInitialized ? isConnected : true,
        reconnect,
        disconnect,
        markIntentionalExit,
      }}
    >
      {children}
      <ReconnectionDialog
        visible={showReconnectDialog}
        disconnectDuration={disconnectDuration}
        onReconnect={handleDialogReconnect}
        onCancel={handleDialogCancel}
      />
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
